/**
 * SystemStatusTab — Status-Dashboard für die Kommandozentrale
 *
 * Zeigt den Zustand aller Systeme auf einen Blick:
 * - Deployment-Status (Vercel)
 * - API-Health-Checks (Supabase, GitHub, Gmail, WinValue, E-Mail)
 * - Agenten-System-Status
 *
 * Ampel-System: OK / Warnung / Fehler
 * Auto-Refresh alle 60 Sekunden
 * Mobile-optimiert
 */

import { RefreshCw, Loader2, Server, Database, Github, Mail, Globe, Activity, Clock, AlertTriangle, CheckCircle2, AlertCircle, FileText } from 'lucide-react';
import {
  useSystemHealth,
  type HealthStatus,
  type ServiceHealth,
  type AgentenHealth,
  type DeploymentHealth,
  type SingleDeploymentHealth,
  type WinvalueRechnungenReport,
} from '@/hooks/useSystemHealth';

// ─── CI Design Tokens (konsistent mit AgentenKommandozentrale) ────────────

const CI = {
  red: '#C41E3A',
  redDark: '#9B1C1C',
  redLight: '#FEF2F2',
  redBorder: '#FECACA',
  bgPanel: '#FFFFFF',
  bgSection: '#F9FAFB',
  bgZebraEven: '#FFFFFF',
  bgZebraOdd: '#F8F9FB',
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  textMuted: '#9CA3AF',
  border: '#E5E7EB',
  borderLight: '#F3F4F6',
  shadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
  emerald: '#059669',
  emeraldBg: '#ECFDF5',
  amber: '#D97706',
  amberBg: '#FFFBEB',
} as const;

// ─── Ampel-Farben ─────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<HealthStatus, { bg: string; text: string; border: string; dot: string; label: string }> = {
  ok:      { bg: '#ECFDF5', text: '#065F46', border: '#A7F3D0', dot: '#059669', label: 'OK' },
  warnung: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A', dot: '#D97706', label: 'Warnung' },
  fehler:  { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA', dot: '#DC2626', label: 'Fehler' },
};

function StatusDot({ status }: { status: HealthStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      className="inline-block w-2 h-2 rounded-full flex-shrink-0"
      style={{ backgroundColor: cfg.dot }}
    />
  );
}

function StatusBadge({ status }: { status: HealthStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
      style={{ backgroundColor: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}` }}
    >
      <StatusDot status={status} />
      {cfg.label}
    </span>
  );
}

function formatZeit(dateStr: string | null | undefined): string {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  const now = Date.now();
  const diffMin = Math.floor((now - d.getTime()) / 60000);
  if (diffMin < 1) return 'Gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min.`;
  if (diffMin < 1440) return `vor ${Math.floor(diffMin / 60)} Std.`;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// ─── Service-Karte ────────────────────────────────────────────────────────

function ServiceCard({
  icon: Icon,
  title,
  service,
}: {
  icon: React.ElementType;
  title: string;
  service: ServiceHealth;
}) {
  const cfg = STATUS_CONFIG[service.status];
  return (
    <div
      className="rounded-lg p-3"
      style={{
        backgroundColor: CI.bgPanel,
        border: `1px solid ${CI.border}`,
        boxShadow: CI.shadow,
      }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: cfg.text }} />
          <span className="text-xs font-semibold" style={{ color: CI.textPrimary }}>{title}</span>
        </div>
        <StatusBadge status={service.status} />
      </div>
      <p className="text-[11px]" style={{ color: CI.textSecondary }}>{service.label}</p>
      {service.details && (
        <p className="text-[10px] mt-0.5" style={{ color: CI.textMuted }}>{service.details}</p>
      )}
      {service.letzterSync && (
        <p className="text-[10px] mt-0.5" style={{ color: CI.textMuted }}>
          <Clock className="w-3 h-3 inline mr-0.5" style={{ verticalAlign: '-2px' }} />
          {formatZeit(service.letzterSync)}
        </p>
      )}
    </div>
  );
}

// ─── Agenten-Karte ────────────────────────────────────────────────────────

function AgentenCard({ agenten }: { agenten: AgentenHealth }) {
  const cfg = STATUS_CONFIG[agenten.status];
  return (
    <div
      className="rounded-lg p-3"
      style={{
        backgroundColor: CI.bgPanel,
        border: `1px solid ${CI.border}`,
        boxShadow: CI.shadow,
      }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4" style={{ color: cfg.text }} />
          <span className="text-xs font-semibold" style={{ color: CI.textPrimary }}>Agenten-System</span>
        </div>
        <StatusBadge status={agenten.status} />
      </div>
      <p className="text-[11px]" style={{ color: CI.textSecondary }}>{agenten.label}</p>
      <div className="flex items-center gap-3 mt-2">
        <div className="flex items-center gap-1">
          <Activity className="w-3 h-3" style={{ color: CI.textMuted }} />
          <span className="text-[10px]" style={{ color: CI.textMuted }}>
            <span className="font-semibold" style={{ color: CI.textSecondary }}>{agenten.aktiveAgenten}</span> aktiv (24h)
          </span>
        </div>
        <div className="flex items-center gap-1">
          <AlertCircle className="w-3 h-3" style={{ color: CI.textMuted }} />
          <span className="text-[10px]" style={{ color: CI.textMuted }}>
            <span className="font-semibold" style={{ color: CI.textSecondary }}>{agenten.offeneVorschlaege}</span> offen
          </span>
        </div>
      </div>
      {agenten.letzterDurchlauf && (
        <p className="text-[10px] mt-1" style={{ color: CI.textMuted }}>
          <Clock className="w-3 h-3 inline mr-0.5" style={{ verticalAlign: '-2px' }} />
          Letzter Durchlauf: {formatZeit(agenten.letzterDurchlauf)}
        </p>
      )}
    </div>
  );
}

// ─── Deployment-Karte ─────────────────────────────────────────────────────

function DeploymentCard({ title, deployment }: { title: string; deployment: SingleDeploymentHealth }) {
  const cfg = STATUS_CONFIG[deployment.status];
  return (
    <div
      className="rounded-lg p-3"
      style={{
        backgroundColor: CI.bgPanel,
        border: `1px solid ${CI.border}`,
        boxShadow: CI.shadow,
      }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Server className="w-4 h-4" style={{ color: cfg.text }} />
          <span className="text-xs font-semibold" style={{ color: CI.textPrimary }}>{title}</span>
        </div>
        <StatusBadge status={deployment.status} />
      </div>
      <p className="text-[11px]" style={{ color: CI.textSecondary }}>{deployment.label}</p>
      {deployment.letztes?.created && (
        <p className="text-[10px] mt-0.5" style={{ color: CI.textMuted }}>
          <Clock className="w-3 h-3 inline mr-0.5" style={{ verticalAlign: '-2px' }} />
          {formatZeit(deployment.letztes.created)}
        </p>
      )}
    </div>
  );
}

// ─── Gesamtstatus-Banner ──────────────────────────────────────────────────

function GesamtstatusBanner({ statuses }: { statuses: HealthStatus[] }) {
  const hasFehler = statuses.includes('fehler');
  const hasWarnung = statuses.includes('warnung');
  const overall: HealthStatus = hasFehler ? 'fehler' : hasWarnung ? 'warnung' : 'ok';
  const cfg = STATUS_CONFIG[overall];

  const Icon = overall === 'ok' ? CheckCircle2 : overall === 'warnung' ? AlertTriangle : AlertCircle;
  const message = overall === 'ok'
    ? 'Alle Systeme laufen normal'
    : overall === 'warnung'
    ? 'Einige Systeme zeigen Auffälligkeiten'
    : 'Systemprobleme erkannt';

  return (
    <div
      className="rounded-lg p-3 flex items-center gap-3"
      style={{
        backgroundColor: cfg.bg,
        border: `1px solid ${cfg.border}`,
      }}
    >
      <Icon className="w-5 h-5 flex-shrink-0" style={{ color: cfg.text }} />
      <div>
        <p className="text-sm font-semibold" style={{ color: cfg.text }}>{message}</p>
        <p className="text-[10px]" style={{ color: cfg.text, opacity: 0.7 }}>
          {statuses.filter(s => s === 'ok').length}/{statuses.length} Dienste OK
        </p>
      </div>
    </div>
  );
}

// ─── Rechnungen-Report-Karte ──────────────────────────────────────────────

function RechnungenReportCard({ report }: { report: WinvalueRechnungenReport }) {
  const stats = [
    { label: 'Akten synchronisiert', value: report.akten_synchronisiert },
    { label: 'Ausstehend',           value: report.akten_ausstehend },
    { label: 'Rechnungen gesamt',    value: report.rechnungen_gesamt },
    { label: 'Neu (24h)',            value: report.neu_letzte_24h },
    { label: 'Geändert (24h)',       value: report.geaendert_letzte_24h },
    { label: 'Fehler (24h)',         value: report.fehler_letzte_24h },
  ];
  return (
    <div
      className="rounded-lg p-3"
      style={{
        backgroundColor: CI.bgPanel,
        border: `1px solid ${CI.border}`,
        boxShadow: CI.shadow,
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <FileText className="w-4 h-4" style={{ color: CI.textSecondary }} />
        <span className="text-xs font-semibold" style={{ color: CI.textPrimary }}>Rechnungen Sync-Report</span>
      </div>
      <div className="grid grid-cols-3 gap-x-3 gap-y-1.5">
        {stats.map(({ label, value }) => (
          <div key={label}>
            <p className="text-[16px] font-bold leading-tight" style={{ color: CI.textPrimary }}>{value}</p>
            <p className="text-[10px]" style={{ color: CI.textMuted }}>{label}</p>
          </div>
        ))}
      </div>
      {report.letzter_sync && (
        <p className="text-[10px] mt-2" style={{ color: CI.textMuted }}>
          <Clock className="w-3 h-3 inline mr-0.5" style={{ verticalAlign: '-2px' }} />
          Letzter Sync: {formatZeit(report.letzter_sync)}
        </p>
      )}
      <p className="text-[10px] mt-0.5" style={{ color: CI.textMuted }}>
        {report.runs_letzte_24h} Cron-Läufe in den letzten 24 Std.
        · {report.akten_mit_winvalue_uid} Akten mit WinValue-UID
      </p>
    </div>
  );
}

// ─── Haupt-Komponente ─────────────────────────────────────────────────────

export default function SystemStatusTab() {
  const { health, loading, error, lastRefresh, refresh } = useSystemHealth();

  if (loading && !health) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: CI.textMuted }} />
        <p className="text-sm" style={{ color: CI.textMuted }}>System-Status wird geladen...</p>
      </div>
    );
  }

  if (error && !health) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertTriangle className="w-8 h-8 mb-3" style={{ color: CI.amber }} />
        <p className="text-sm font-medium" style={{ color: CI.textPrimary }}>Status nicht verfügbar</p>
        <p className="text-xs mt-1" style={{ color: CI.textMuted }}>{error}</p>
        <button
          onClick={refresh}
          className="mt-3 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
          style={{ color: CI.redDark, backgroundColor: CI.redLight }}
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (!health) return null;

  const allStatuses: HealthStatus[] = [
    health.supabase.status,
    health.github.status,
    health.gmail.status,
    health.winvalue.status,
    health.winvalueRechnungen?.status ?? 'warnung',
    health.email.status,
    health.agenten.status,
    health.deployments.production.status,
    health.deployments.preview.status,
  ];

  return (
    <div className="space-y-4">
      {/* Header mit Refresh */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold" style={{ color: CI.textPrimary }}>System-Status</h3>
        <div className="flex items-center gap-2">
          {lastRefresh && (
            <span className="text-[10px]" style={{ color: CI.textMuted }}>
              Aktualisiert: {lastRefresh.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
            </span>
          )}
          <button
            onClick={refresh}
            className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors"
            title="Jetzt aktualisieren"
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = CI.bgSection; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} style={{ color: CI.textMuted }} />
          </button>
        </div>
      </div>

      {/* Gesamtstatus */}
      <GesamtstatusBanner statuses={allStatuses} />

      {/* Deployments */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: CI.textMuted }}>
          Deployments
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <DeploymentCard title="Production" deployment={health.deployments.production} />
          <DeploymentCard title="Preview" deployment={health.deployments.preview} />
        </div>
      </div>

      {/* API Health */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: CI.textMuted }}>
          API-Verbindungen
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <ServiceCard icon={Database} title="Supabase" service={health.supabase} />
          <ServiceCard icon={Github} title="GitHub API" service={health.github} />
          <ServiceCard icon={Mail} title="Gmail Sync" service={health.gmail} />
          <ServiceCard icon={Globe} title="WinValue Sync" service={health.winvalue} />
          {health.winvalueRechnungen && (
            <ServiceCard icon={FileText} title="WinValue Rechnungen" service={health.winvalueRechnungen} />
          )}
          <ServiceCard icon={Mail} title="E-Mail Sync" service={health.email} />
        </div>
      </div>

      {/* Rechnungen Report */}
      {health.winvalueRechnungenReport && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: CI.textMuted }}>
            WinValue Rechnungen
          </p>
          <RechnungenReportCard report={health.winvalueRechnungenReport} />
        </div>
      )}

      {/* Agenten-System */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: CI.textMuted }}>
          Agenten-System
        </p>
        <AgentenCard agenten={health.agenten} />
      </div>

      {/* Auto-Refresh Hinweis */}
      <p className="text-[10px] text-center pt-2" style={{ color: CI.textMuted }}>
        Auto-Refresh alle 60 Sekunden
      </p>
    </div>
  );
}
